import type { PageComponentType } from '@/core/features/a-dynamic-page/dynamic-page-pacts/dynamic-page-type';
import type { Deviceoutput } from '@app/core-features/cookies/cookies-service';
import type {
    IUnifiedLoginLayerResponse,
    IUnifiedLoginResponseData,
} from '@app/core-features/wireframe/wireframe-types';

import logger from '@app/core-features/logger/logger';

import { WIREFRAME_ULI_LOGIN_LAYER_EVENT, WIREFRAME_UNIFIED_LOGIN_EVENT } from '@/core/features/wireframe/constants';
import { getURL } from '@/core/utils/url';

// unified Login integration docs: https://confluence.check24.de/display/HOULI/Integration+Guide

function checkUnifiedLoginLayerEventSuccess(state?: string): boolean {
    return state !== undefined && state !== null && state !== '' && state !== 'browserback';
}

function generateBannerUrl(url: string) {
    // Keep explicit type to yield an error if the component is removed
    const filteredComponents: PageComponentType[] = ['CheckoutHeaderV1'];

    const parsedUrl = getURL(url);
    parsedUrl.searchParams.set('componentTypeFilter', filteredComponents.join(','));
    parsedUrl.searchParams.set('isWireframeDisabled', 'true');
    parsedUrl.searchParams.set('isHiddenMenuDisabled', 'true');
    parsedUrl.searchParams.set('deviceoutput', 'app' satisfies Deviceoutput);

    return parsedUrl.href;
}

export function registerUnifiedLogin({
    onError,
    onSuccess,
    triggerOnce = false,
    withLoginLayer = false,
}: {
    onError?: (event: IUnifiedLoginResponseData) => void;
    onSuccess?: (event: IUnifiedLoginResponseData) => void;
    triggerOnce?: boolean;
    withLoginLayer?: boolean;
}) {
    const eventHandler = (event: unknown) => {
        const unifiedLoginEvent = (event as IUnifiedLoginLayerResponse)?.detail?.ul;

        if (unifiedLoginEvent.BROWSERBACK) {
            history.go(-1);
        }

        if (checkUnifiedLoginLayerEventSuccess(unifiedLoginEvent?.STATE)) {
            onSuccess?.(unifiedLoginEvent);
        } else {
            onError?.(unifiedLoginEvent);
        }
    };

    if (withLoginLayer) {
        addEventListener(WIREFRAME_ULI_LOGIN_LAYER_EVENT, eventHandler, { once: triggerOnce });
        return () => removeEventListener(WIREFRAME_ULI_LOGIN_LAYER_EVENT, eventHandler);
    } else {
        addEventListener(WIREFRAME_UNIFIED_LOGIN_EVENT, eventHandler, { once: triggerOnce });
        return () => removeEventListener(WIREFRAME_UNIFIED_LOGIN_EVENT, eventHandler);
    }
}

export function openUnifiedLogin({
    clientUrl,
    isApp,
    onUnifiedLoginTriggered,
    retries = 0,
}: {
    clientUrl?: string;
    isApp: boolean;
    onUnifiedLoginTriggered?: () => {};
    retries?: number;
}) {
    if (retries > 3) {
        logger.error('Could not open Unified Login after 3 retries, aborting');
        return;
    }
    if (isApp) {
        if (window.C24App?.unifiedLoginTriggered) {
            return;
        }
        if (!window.C24App?.triggerUnifiedLoginWithOptions) {
            setTimeout(() => {
                openUnifiedLogin({ clientUrl, isApp, retries: retries + 1 });
            }, 2000);
            return;
        }
        const bannerUrl = clientUrl ? generateBannerUrl(clientUrl) : undefined;
        window.C24App.triggerUnifiedLoginWithOptions({ bannerUrl, configurationType: 'app', email: null });
        onUnifiedLoginTriggered?.();
    } else {
        window.Check24?.uliloginlayer.open();
    }
}
